import * as React from 'react'

import { z } from 'zod'
export const schema = z.object({
  id: z.number(),
  title: z.string(),
  proposalId: z.number(),
  quotationId: z.number(),
  offerId: z.number(),
  dueDate: z.string(),
  status: z.string(),
  editDate: z.string(),
  permissions: z.array(
    z.object({
      id: z.number(),
      user: z.object({
        id: z.number(),
        firstname: z.string(),
        lastname: z.string(),
        email: z.string(),
      }),
    }),
  ),
})
import api from '@/lib/api'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  useDisclosure,
} from '@nextui-org/react'
import { Icon } from '@iconify/react'
import { useSocketContextData } from '@/context/socket'
import dayjs from 'dayjs'
import { format } from 'date-fns'
import OverView from './components/Overview'
import { PowerDocsEventsName } from 'src/types/SocketEvents'
import { useWorkspaceContextData } from 'src/context/workspace'
import RSelect from 'react-select'
import toast from 'react-hot-toast'
import TenderAttachments from './components/Attachments'
import Requirements from './components/Requirements'
import { useSubscriptionContext } from 'src/context/subscription'
import { SubscribeToEvaluate } from 'src/components/modals/subscribe-to-evaluate'

interface MemberUser {
  id: number
  firstname: string
  lastname: string
  email: string
}
interface Member {
  id: number
  role: 'OWNER' | 'EDITOR' | 'VIEWER'
  user: MemberUser
}
const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

export default function TenderView({
  tabSlug = 'overviewgit',
  isAdmin = false,
}: {
  tabSlug?: string
  isAdmin?: boolean
}) {
  const [tender, setTender] = useState<any>({})
  const [_, setInitialized] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>(tabSlug.charAt(0).toUpperCase() + tabSlug.slice(1))
  const { tenderUUID } = useParams()
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const [savingTender, setSavingTender] = useState<boolean>(false)
  const [scoreTender, setScoreTender] = useState<number>(0)
  const { currentLimitations } = useSubscriptionContext()
  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: openSubscriptionModal,
    onClose: closeSubscriptionModal,
  } = useDisclosure()

  const loadTender = async () => {
    const res = await api.get(`/tenders/${tenderUUID}`)
    setTender(res.data)
    setInitialized(true)
  }
  const navigate = useNavigate()
  const { io } = useSocketContextData()
  const { activeWorkspace } = useWorkspaceContextData()
  const converToPowerDoc = async () => {
    if (currentLimitations) {
      if (
        currentLimitations.isFreeTrial ||
        (currentLimitations?.hasSubscription && currentLimitations.currentSubscription === 'EVALUATE') ||
        currentLimitations.currentSubscription === 'EXECUTE'
      ) {
        if (
          currentLimitations.currentSubscription === 'EXECUTE' ||
          currentLimitations.currentSubscription === 'EVALUATE'
        ) {
          setIsGenerating(true)
          const res = await io.emitWithAck(PowerDocsEventsName.ADD_TENDER_TO_PIPELINE, {
            token: localStorage.getItem('accessToken'),
            workspaceUUID: activeWorkspace,
            tenders: [tenderUUID],
          })
          if (res.message) {
            toast.success(res.message)
            setIsGenerating(false)
          } else {
            if (res.length > 0 && res?.[0]?.uuid) {
              navigate('/client/' + (tender.buyer ?? 'powerdocs') + '/project/' + res?.[0]?.uuid + '/documents')
            }
          }
        } else {
          openSubscriptionModal()
        }
      } else {
        openSubscriptionModal()
      }
    }
  }
  useEffect(() => {
    loadTender()
  }, [])

  const { profile } = useWorkspaceContextData()
  const [users, setUsers] = useState<{ id: number; firstname: string; lastname: string; email: string }[]>([])
  const [selectedClient, setSelectedClient] = useState<any>(null)
  const { isOpen: openedAssignToClient, onOpenChange: onOpenAssignToClientChange } = useDisclosure()
  const getUsers = async () => {
    const res = await api.get('/admin/users')
    setUsers(res.data ?? [])
  }
  useEffect(() => {
    if (isAdmin && profile?.isAdmin) {
      getUsers()
    }
  }, [isAdmin, profile])

  const assignTenderToClient = async () => {
    setSavingTender(true)
    await io.emitWithAck(PowerDocsEventsName.ADD_TENDER_TO_USER, {
      token: localStorage.getItem('accessToken'),
      tenders: [tenderUUID],
      score: scoreTender,
      clientId: selectedClient,
    })
    setSavingTender(false)
    onOpenAssignToClientChange()
    toast.success('Tenders have been assigned')
  }

  return (
    <div className="flex flex-col w-full max-w-screen-xl h-screen p-3">
      <Breadcrumbs className={'pl-2 pt-4'}>
        <BreadcrumbItem onClick={() => navigate('/tenders')}>
          <Icon icon={'lucide:home'} /> Public Tenders
        </BreadcrumbItem>
      </Breadcrumbs>
      <div className="flex w-full pt-8 pb-6 px-2 2xl:px-8">
        <header className="flex flex-col w-full gap-4">
          <div className="w-fit flex flex-row items-center gap-2">
            <div className={'w-fit flex items-center gap-2 min-h-[36px]'}>
              <Icon
                icon={'lucide:square-chevron-left'}
                width={25}
                color={'#71717a'}
                className="cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <h1 className="text-xl font-bold text-default-900 capitalize">Public Tenders ▸ {tender?.title}</h1>
            </div>
          </div>
          <div className={'flex flex-row gap-4'}>
            <div className={'border-1 border-default rounded-md pt-1 pb-1 pl-4 pr-4 flex flex-row gap-2'}>
              <Icon icon={'lucide:handshake'} width={16} color={'#71717a'} />
              <span className={'text-xs'}>Buyer: {tender.buyer ?? 'No Buyer Information'}</span>
            </div>

            <div className={'border-1 border-default rounded-md pt-1 pb-1 pl-4 pr-4 flex flex-row gap-2'}>
              <Icon icon={'lucide:calendar-days'} width={16} color={'#71717a'} />
              <span className={'text-xs'}>
                {tender.dueDate
                  ? `${format(tender.deadline, 'PPP')} | ${dayjs(tender.deadline).fromNow()}.`
                  : 'No due date specified yet'}
              </span>
            </div>
          </div>
        </header>
      </div>
      <div className="flex flex-1 w-full pb-2 max-w-full max-h-full overflow-y-auto flex-col px-2 2xl:px-8">
        <div className="flex w-full justify-between">
          <div className={'p-1 rounded-xl w-fit bg-default-100'}>
            {['Overview', 'Requirements', 'Attachments'].map((item) => (
              <Button
                key={item}
                isDisabled={isGenerating}
                size={'sm'}
                className={activeTab.toLowerCase() === item.toLowerCase() ? activeButtonClass : inActiveButtonClass}
                onPress={() => {
                  setActiveTab(item)
                }}
              >
                {item}
              </Button>
            ))}
          </div>
          <div>
            {isAdmin && profile?.isAdmin ? (
              <Button
                color="secondary"
                onClick={onOpenAssignToClientChange}
                startContent={<Icon icon="lucide:circle-plus" />}
              >
                Asign tenders to User
              </Button>
            ) : (
              <Button
                onClick={converToPowerDoc}
                color="secondary"
                startContent={
                  isGenerating ? <Spinner color="white" size="sm" /> : <Icon icon="lucide:circle-plus" color="#fff" />
                }
              >
                {isGenerating ? 'Adding to pipeline' : 'Add to pipeline'}
              </Button>
            )}
          </div>
        </div>
        {/*activeTab === 'Documents' && <DocumentList tender={tender}/>*/}
        {activeTab === 'Overview' && <OverView tender={tender} />}
        {activeTab === 'Requirements' && <Requirements loadTender={loadTender} tender={tender} />}
        {activeTab === 'Attachments' && <TenderAttachments attachments={tender?.attachments ?? []} />}
      </div>
      <SubscribeToEvaluate isOpen={isSubscriptionModalOpen} onClose={closeSubscriptionModal} />
      <Modal
        isOpen={openedAssignToClient}
        onOpenChange={onOpenAssignToClientChange}
        backdrop="blur"
        size="xl"
        className="z-[1000] select-dialog"
        isDismissable={false}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Asign to Client</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-4">
                  <Input
                    labelPlacement="outside"
                    placeholder="e.g 80%"
                    label="Matching Score"
                    min={0}
                    max={100}
                    type="number"
                    onChange={(e) => setScoreTender(e.target.value)}
                  />
                  <div className={' relative flex flex-col gap-1'}>
                    <span className={'text-medium ml-1 text-sm'}>Merge with</span>
                    <RSelect
                      className={'flex flex-1 w-full'}
                      onChange={(option) => setSelectedClient(option?.value)}
                      placeholder={`Assign tenders to client`}
                      options={users
                        .filter((item) => item.id !== profile.uuid)
                        .map((client) => ({
                          value: client.id,
                          label: [client.firstname ?? '', client.lastname ?? '', ' - ', client.email].join(' ').trim(),
                        }))}
                      styles={{
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? '#3D969E' : '#fff',
                          color: state.isFocused ? '#fff' : '#333',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#333',
                          fontWeight: '500',
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '0',
                        }),
                        control: (base, state) => ({
                          ...base,
                          width: '100%', // Ensure the control spans the full width
                          minWidth: '100%',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          border: state.isFocused ? '1px solid #000' : '1px solid #E0E0E0',
                          boxShadow: state.isFocused ? '0 0 4px rgba(0, 114, 245, 0.3)' : 'none',
                          padding: '4px',
                          transition: 'border 0.2s ease, box-shadow 0.2s ease',
                        }),
                        menu: (base) => ({
                          ...base,
                          width: '100%', // Dropdown menu spans full width
                          minWidth: '100%',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          overflow: 'hidden',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                      }}
                      menuPortalTarget={window.document.querySelector('body')}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onPress={assignTenderToClient} disabled={savingTender}>
                  {savingTender ? 'Saving ...' : 'Assign'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  )
}
